import { useLayoutEffect, useRef, useState, type RefObject } from 'react'

import {
  placeDropPanel,
  placeSidePanel,
  type DropPanelPlacement,
  type DropPanelRequest,
  type SidePanelPlacement,
  type SidePanelRequest,
} from './panelPlacement'

/**
 * THE OTHER HALF OF A PORTALED POPOVER'S LIFE: staying where it belongs.
 *
 * `popoverDismissal.ts` beside this owns closing, and says why: eight
 * components had carried that effect privately, so a fix to any part of it had
 * to be discovered and applied eight times. Placement had no owner at all, and
 * ELEVEN components carried the same effect — measure the trigger, ask
 * `panelPlacement` where the panel goes, then follow it on `resize` and on
 * `scroll` with capture, tearing both down on close.
 *
 * The following is not a refinement. Every one of these panels is
 * `position: fixed` and portaled to `<body>`, so nothing in the layout moves it
 * when the page under it scrolls: without the listeners the panel simply stays
 * behind while its trigger walks away. Which makes the eleven copies the
 * expensive kind — a scroll container that is not the document, a resize
 * observer instead of a window listener, a `visualViewport` correction for a
 * touch keyboard, all of them changes that must be found in eleven files, and
 * nothing fails when three are missed.
 *
 * Both hooks place in a LAYOUT effect, before the browser paints. Nine of the
 * eleven placed in an ordinary one and so rendered nothing on the first frame
 * after opening; the three that measure themselves had already worked out that
 * a panel positioned after the paint is one the reader watches arrive.
 */

/**
 * The listeners alone, for a panel that works out its own geometry.
 *
 * `LabTrendTooltip` is the one that does: it moves between OPPOSITE sides of a
 * point on a chart, which no dropping panel does, and `panelPlacement`
 * deliberately does not carry that flip. What it shares with the other ten is
 * exactly this — WHEN to place again — so this is the half it takes.
 *
 * `place` is read through a ref, so the listeners attach once per opening
 * rather than once per render. A caller whose geometry depends on something
 * that can change while the panel stands open names it in `placeAgainOn`.
 */
export function useAnchorFollow({
  open,
  place,
  placeAgainOn,
}: {
  open: boolean
  place: () => void
  /** A value whose change re-places the panel — the reading-scale dial, say. */
  placeAgainOn?: unknown
}): void {
  const latest = useRef(place)
  latest.current = place

  useLayoutEffect(() => {
    if (!open) {
      return undefined
    }
    const follow = () => latest.current()
    follow()
    window.addEventListener('resize', follow)
    // CAPTURE, because the scroll that moves the trigger is usually not the
    // document's: it is a table body, a side panel, a dialog. A scroll event
    // does not bubble, so the capturing phase on `window` is the only way to
    // hear one from a container this popover has never heard of.
    window.addEventListener('scroll', follow, true)
    return () => {
      window.removeEventListener('resize', follow)
      window.removeEventListener('scroll', follow, true)
    }
  }, [open, placeAgainOn])
}

/**
 * Where a dropping panel goes, and where it keeps going for as long as it is
 * open. Ten of the eleven want this one.
 *
 * It answers null while the panel is closed and until the trigger has been
 * measured, which is what every caller already renders on: `open && placement`.
 * A panel that must be RENDERED to be measured — a menu placed by its own
 * height — draws itself hidden and applies the answer when it arrives.
 *
 * `request` is handed the trigger's rect and answers the request
 * `placeDropPanel` takes, so the width floor a combobox derives from its own
 * trigger and the height a measured menu reports stay with the caller that
 * knows them. Answering null declines to place this time round, which is how a
 * panel waiting on its own measurement says so.
 */
export function usePopoverPlacement<T extends Element>({
  open,
  anchor,
  request,
  placeAgainOn,
}: {
  open: boolean
  /** The control the panel drops from. */
  anchor: RefObject<T | null>
  request: (anchor: DOMRect) => DropPanelRequest | null
  placeAgainOn?: unknown
}): DropPanelPlacement | null {
  const [placement, setPlacement] = useState<DropPanelPlacement | null>(null)
  const latest = useRef({ anchor, request })
  latest.current = { anchor, request }

  useAnchorFollow({
    open,
    place: () => {
      const rect = latest.current.anchor.current?.getBoundingClientRect()
      if (rect === undefined) {
        return
      }
      const wanted = latest.current.request(rect)
      if (wanted === null) {
        return
      }
      setPlacement(placeDropPanel(wanted))
    },
    placeAgainOn,
  })

  /*
   * A CLOSED PANEL HAS NO PLACE, and all eleven said so — several of them from
   * inside a private `close`, which then had to be called from each of the
   * three or four ways that popover could be closed, and one of which cleared
   * it from two places because it could not. Answering null here says it once:
   * whoever set `open` false has said everything there is to say.
   *
   * The last answer stays in state rather than being cleared, because clearing
   * it would cost a render on every close to hide a value nobody can reach.
   * Reopening places again in a layout effect, before the paint, so the stale
   * one is never on screen.
   */
  return open ? placement : null
}

/**
 * The place BESIDE an anchor — `placeSidePanel`'s geometry, kept current the
 * same way the dropping one is. The panel is measured rather than declared,
 * because a hint is as wide as its words and a hover card as tall as what it
 * folds; so the first render places nothing, the layout effect measures the
 * box the browser has just been handed, and one frame later measures it
 * again, because a panel read before its first layout reports no size and
 * would be placed as if it had none.
 *
 * The anchor is EITHER an element or a ref to one. A ref is the ordinary
 * case, a control the caller renders itself; the element is for a panel that
 * travels — the rail moves one hint from glyph to glyph as the pointer goes,
 * so its anchor is state that changes while the panel stays mounted, and a
 * change of it is a reason to place again. `side` may likewise be a function
 * of the anchor, for a panel whose side is a fact about where the anchor
 * stands rather than about the panel.
 */
export function useSidePlacement({
  open,
  anchor,
  panel,
  side,
  gap,
}: {
  open: boolean
  anchor: HTMLElement | RefObject<HTMLElement | null> | null
  panel: RefObject<HTMLElement | null>
  side: SidePanelRequest['side'] | ((anchor: HTMLElement) => SidePanelRequest['side'])
  gap: number
}): SidePanelPlacement | null {
  const [placement, setPlacement] = useState<SidePanelPlacement | null>(null)
  const latest = useRef({ anchor, panel, side, gap })
  latest.current = { anchor, panel, side, gap }
  // The element case is the one whose identity is a placement input; a ref's
  // current is read at placing time and its object never changes.
  const travelling = anchor instanceof HTMLElement ? anchor : null

  const place = () => {
    const { anchor, panel, side, gap } = latest.current
    const element = anchor instanceof HTMLElement ? anchor : (anchor?.current ?? null)
    const box = panel.current?.getBoundingClientRect()
    if (element === null || box === undefined) {
      return
    }
    setPlacement(
      placeSidePanel({
        anchor: element.getBoundingClientRect(),
        gap,
        side: typeof side === 'function' ? side(element) : side,
        width: box.width,
        height: box.height,
      }),
    )
  }
  useAnchorFollow({ open, place, placeAgainOn: travelling })

  useLayoutEffect(() => {
    if (!open) return undefined
    const frame = window.requestAnimationFrame(place)
    return () => window.cancelAnimationFrame(frame)
    // `place` reads everything through `latest`; a travelling anchor is the
    // one input whose change means a new box to measure against.
  }, [open, travelling])

  return open ? placement : null
}
